import { type JSX, useEffect, useState } from "react";

import type { JobHandle } from "../api/catalog";
import { type JobSnapshot, streamJob } from "../api/jobs";
import { JsonView } from "./JsonView";

interface JobProgressProps {
  callId: string;
  job: JobHandle;
}

/**
 * Watches an accepted job and shows where it has got to.
 *
 * The tester never copies a job id or calls a status endpoint: this opens as soon as the
 * operation is accepted and closes itself when the job finishes.
 */
export function JobProgress({ callId, job }: JobProgressProps): JSX.Element {
  const [snapshot, setSnapshot] = useState<JobSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [watching, setWatching] = useState(job.pollable);

  useEffect(() => {
    if (!job.pollable) {
      return;
    }
    setSnapshot(null);
    setError(null);
    setWatching(true);

    const close = streamJob(callId, {
      onSnapshot: setSnapshot,
      onError: setError,
      onClose: () => setWatching(false),
    });
    return close;
  }, [callId, job.pollable]);

  if (!job.pollable) {
    return (
      <div className="notice notice--warning">
        <strong>Accepted, with nothing to poll.</strong> The Memory Service exposes no status
        endpoint for this one, so the response body and the checks below are how you tell whether
        it did anything.
      </div>
    );
  }

  return (
    <div className={`notice notice--${toneOf(snapshot, error)}`}>
      <strong>{headlineOf(snapshot, error, watching)}</strong>

      {error && <p>{error}</p>}

      {snapshot && (
        <>
          <p className="muted">
            {snapshot.state ?? "unknown"} · {snapshot.poll_count} check
            {snapshot.poll_count === 1 ? "" : "s"} · {snapshot.elapsed_seconds}s
            {snapshot.stage && <> · {snapshot.stage}</>}
          </p>

          {snapshot.percent !== null && (
            <progress className="job__bar" value={snapshot.percent} max={100} />
          )}

          {snapshot.error && <p className="job__error">{snapshot.error}</p>}
          {snapshot.detail && <p className="muted">{snapshot.detail}</p>}

          {snapshot.finished && snapshot.payload !== null && (
            <JsonView value={snapshot.payload} openDepth={2} />
          )}
        </>
      )}
    </div>
  );
}

function toneOf(snapshot: JobSnapshot | null, error: string | null): string {
  if (error) {
    return "error";
  }
  switch (snapshot?.outcome) {
    case "succeeded":
      return "info";
    case "failed":
      return "error";
    case "partial":
    case "unknown":
      return "warning";
    default:
      return "info";
  }
}

function headlineOf(
  snapshot: JobSnapshot | null,
  error: string | null,
  watching: boolean,
): string {
  if (error) {
    return "Could not watch this job.";
  }
  if (!snapshot) {
    return watching ? "Accepted — waiting for the first update…" : "Accepted.";
  }
  switch (snapshot.outcome) {
    case "succeeded":
      return "Finished.";
    case "partial":
      return "Finished, partly.";
    case "failed":
      return "The job failed.";
    case "unknown":
      return "Stopped watching.";
    default:
      return "Running…";
  }
}
